import { isElement, isText, unwrapNode } from './dom/node';
import { isBreakElement } from './dom/paragraph';
import { isSelectionPresent } from './selection';

export const INSERTION_POINT_FILLER = '\u200b';
const INSERTION_POINT_FILLER_TRIM_REGEXP = /^\u200b/;

export function trimInsertionPointFiller(value: string): string {
  return value.replace(INSERTION_POINT_FILLER_TRIM_REGEXP, '');
}

export function createInsertionPoint(document: Document) {
  const insertionPoint = document.createElement('span');
  insertionPoint.setAttribute('data-editor-role', 'insertion-point');
  return insertionPoint;
}

export function isInsertionPoint(node: Node | null): boolean {
  return !!node && isElement(node) && node.getAttribute('data-editor-role') === 'insertion-point';
}

export function queryInsertionPoints(element: Element): Node[] {
  return Array.from(element.querySelectorAll('[data-editor-role="insertion-point"]'));
}

export function removeInsertionPointIfStale(node: Node): void {
  if (!isInsertionPoint(node)) {
    throw new Error('node is not an insertion point');
  }

  const document = node.ownerDocument;
  if (!document || !node.parentNode) {
    return;
  }

  const selection = document.getSelection();
  const isCursorInInsertionPoint =
    isSelectionPresent(selection) &&
    node.contains(selection.anchorNode) &&
    node.contains(selection.focusNode);

  // A nonTrivialNode either contains text, or is a <br> tag.
  const nonTrivialNode = document
    .createTreeWalker(node, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
      acceptNode: (candidateNode: Node) => {
        if (isText(candidateNode)) {
          return trimInsertionPointFiller(candidateNode.data).length > 0
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }

        if (isBreakElement(candidateNode)) {
          return NodeFilter.FILTER_ACCEPT;
        }

        // We skip non-break elements, because we want to keep walking through
        // the tree to check if there is non-trivial text or a break element
        // somewhere nested within this element.
        return NodeFilter.FILTER_SKIP;
      },
    })
    .nextNode();

  if (isCursorInInsertionPoint && !nonTrivialNode) {
    return;
  }

  const isCursorAtEnd =
    !!nonTrivialNode &&
    isText(nonTrivialNode) &&
    !!selection &&
    selection.isCollapsed &&
    selection.focusNode === nonTrivialNode &&
    selection.focusOffset === nonTrivialNode.length;

  if (nonTrivialNode) {
    if (isText(nonTrivialNode)) {
      // Strip the filler.
      nonTrivialNode.data = trimInsertionPointFiller(nonTrivialNode.data);
    }
    // Unwrap the insertion point.
    unwrapNode(node);
  } else if (node.parentNode) {
    // Remove the insertion point entirely.
    node.parentNode.removeChild(node);
  }

  // Restore the cursor.
  if (nonTrivialNode && isText(nonTrivialNode) && selection && isCursorAtEnd) {
    selection.collapse(nonTrivialNode, nonTrivialNode.length);
  }
}
